import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// Detect browser language or use stored preference
const getInitialLanguage = (): string => {
  // Check localStorage first
  const stored = localStorage.getItem("language");
  if (stored && (stored === "en" || stored === "fr")) {
    return stored;
  }

  // Check browser language
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith("fr")) {
    return "fr";
  }

  // Default to French (Quebec-based restaurant)
  return "fr";
};

const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        home: "Home",
        montreal: "Montreal",
        delson: "Delson",
        menu: "Menu",
        order: "Order",
        contact: "Contact",
        delivery: "Delivery",
      },

      // Common actions and labels
      common: {
        backToHome: "Back to Home",
        learnMore: "Learn More",
        orderNow: "Order Now",
        viewMenu: "View Menu",
        orderPickup: "ORDER PICKUP",
        orderDelivery: "ORDER DELIVERY",
        reservations: "Reservations",
        call: "CALL",
        readyToOrder: "READY TO ORDER?",
        experienceGreekCuisine: "Experience authentic Greek cuisine today",
        skipToContent: "Skip to Content",
        allRightsReserved: "All rights reserved",
        popular: "Popular",
        spicy: "Spicy",
        vegetarian: "Vegetarian",
        openMenu: "Open Menu",
        closeMenu: "Close Menu",
        toggleMenuCategories: "Toggle menu categories",
      },

      // Language switcher
      language: {
        label: "Language",
        en: "English",
        fr: "Français",
        switchTo: "Switch to",
      },

      // Locations
      locations: {
        chooseLocation: "CHOOSE YOUR LOCATION",
        montreal: {
          name: "Montréal",
          address: "2532 St Catherine St E\nMontreal, QC H2K 2K2",
          phone: "514-527-2532",
          hours: "Tue-Sun 11AM–9PM, Mon Closed",
          tagline:
            "Discover our Montreal menu featuring Lunch & Dinner selections",
        },
        delson: {
          name: "Delson",
          address: "35 Boulevard Georges-Gagné Sud\nDelson, QC J5B 2E4",
          phone: "450-632-5320",
          hours: "Wed 4PM–8:30PM, Thu-Sun 11AM–9PM, Mon-Tue Closed",
          tagline: "Explore our Delson menu with traditional Greek favorites",
        },
      },

      // Home page
      home: {
        heroTitle: "AUTHENTIC GREEK CUISINE",
        heroDescription:
          "Fresh Mediterranean dishes made daily with traditional recipes and quality ingredients. Serving Montréal and Delson with warm Greek hospitality.",
        byow: "Bring your own wine",
      },

      // Location page
      location: {
        description:
          "Located conveniently in the heart of {{location}}, LA CAVA GRECQUE is an authentic Greek Mediterranean grill serving up fresh souvlaki, gyros, moussaka, Greek salads and more in a warm, traditional setting",
        featuredTitle: "FEATURED DISHES",
        featuredSubtitle: "Taste the authentic flavors of Greece",
        aboutTitle: "ABOUT LA CAVA GRECQUE",
        aboutText1:
          "Welcome to LA CAVA GRECQUE, where traditional Greek cuisine meets {{location}}'s vibrant food scene. For years, we've been serving authentic Mediterranean dishes prepared with the freshest ingredients and time-honored recipes passed down through generations.",
        aboutText2:
          "Our commitment is simple: deliver the true taste of Greece in every dish. From our perfectly grilled souvlaki to our homemade tzatziki, each item on our menu is crafted with passion and respect for Greek culinary traditions.",
        aboutText3:
          "Whether you're dining in, picking up, or having food delivered, we promise an authentic Greek experience that will transport your taste buds straight to the Mediterranean.",
      },

      // Featured dishes
      featured: {
        souvlaki: {
          name: "SOUVLAKI",
          description:
            "Tender marinated meat grilled to perfection, served with pita, tzatziki, and fresh vegetables",
        },
        gyros: {
          name: "GYROS",
          description:
            "Savory sliced meat wrapped in warm pita with onions, tomatoes, and our signature tzatziki sauce",
        },
        greekSalad: {
          name: "GREEK SALAD",
          description:
            "Fresh tomatoes, cucumbers, olives, and feta cheese with authentic Greek olive oil and herbs",
        },
        moussaka: {
          name: "MOUSSAKA",
          description:
            "Layers of eggplant, seasoned meat, and béchamel sauce baked to golden perfection",
        },
      },

      // Menu categories (sections)
      menu: {
        title: "MENU",
        categories: {
          specials: "Specials",
          lunch: "Lunch",
          dinner: "Dinner",
          grocery: "Grocery",
          "new-arrivals": "New Arrivals",
        },
        sections: {
          lunch: "LUNCH",
          dinner: "DINNER",
          "new-arrivals": "NEW ARRIVALS",
          specials: "SPECIALS",
          grocery: "GROCERY",
        },
      },

      // Submenu labels
      submenus: {
        appetizers: "Appetizers",
        "main-dishes": "Main Dishes",
        pitas: "Pitas",
        salads: "Meal Salads",
        pasta: "Pasta",
        extras: "Extras",
        beverages: "Beverages",
        desserts: "Desserts",
        "kids-menu": "Kids Menu (12 and under)",
        "special-cava": "Cava Special",
        "red-meat": "Red Meat",
        seafood: "Seafood",
        "surf-turf": "Surf & Turf",
        "cava-super-bowls": "Cava Super Bowls",
        "cava-mini-pitas": "Cava Mini Pitas",
        "cava-new-plates": "Cava New Plates",
        "cava-burgers": "Cava Burgers",
        "cava-poutines": "Cava Poutines",
        "for-two": "For Two",
        "chef-special": "Chef's Special",
        "lobster-festival": "Lobster Festival",
        "mothers-day-special": "Mother's Day Special",
        "new-pita-specials": "New Pita Specials",
        soups: "Soups",
        dips: "Dips",
        sauces: "Sauces",
        "prepared-meals": "Prepared Meals",
      },

      // Order page
      order: {
        title: "ORDER ONLINE",
        subtitle: "Choose your preferred location to start your order",
        pickupTitle: "PICKUP",
        pickupDescription: "Order ahead and pick up at your preferred location",
        deliveryTitle: "DELIVERY",
        deliveryDescription:
          "Get your favorite Greek dishes delivered to your door",
        comingSoon: "Online ordering coming soon!",
        callToOrder: "In the meantime, call us to place your order:",
      },

      // 404 page
      notFound: {
        title: "404",
        subtitle: "Page Not Found",
        description:
          "The page you're looking for doesn't exist or has been moved.",
        backHome: "BACK TO HOME",
      },

      // Footer
      footer: {
        copyright: "© {{year}} LA CAVA GRECQUE. All rights reserved.",
      },
    },
  },
  fr: {
    translation: {
      // Navigation
      nav: {
        home: "Accueil",
        montreal: "Montréal",
        delson: "Delson",
        menu: "Menu",
        order: "Commander",
        contact: "Contact",
        orderDelivery: "Livraison",
        reservations: "Réservations",
      },

      // Common actions and labels
      common: {
        backToHome: "Retour à l'accueil",
        learnMore: "En savoir plus",
        orderNow: "Commander",
        viewMenu: "Voir le menu",
        orderPickup: "POUR EMPORTER",
        orderDelivery: "LIVRAISON",
        reservations: "Réservations",
        call: "APPELER",
        readyToOrder: "PRÊT À COMMANDER?",
        experienceGreekCuisine:
          "Découvrez la cuisine grecque authentique aujourd'hui",
        skipToContent: "Passer au contenu",
        allRightsReserved: "Tous droits réservés",
        popular: "Populaire",
        spicy: "Épicé",
        vegetarian: "Végétarien",
        openMenu: "Ouvrir le menu",
        closeMenu: "Fermer le menu",
        toggleMenuCategories: "Basculer les catégories du menu",
      },

      // Language switcher
      language: {
        label: "Langue",
        en: "English",
        fr: "Français",
        switchTo: "Changer pour",
      },

      // Locations
      locations: {
        chooseLocation: "CHOISISSEZ VOTRE EMPLACEMENT",
        montreal: {
          name: "Montréal",
          address: "2532 rue Ste-Catherine E\nMontréal, QC H2K 2K2",
          phone: "514-527-2532",
          hours: "Mar-Dim 11H–21H, Lun Fermé",
          tagline:
            "Découvrez notre menu Montréal avec les sélections Midi & Soir",
        },
        delson: {
          name: "Delson",
          address: "35 Boulevard Georges-Gagné Sud\nDelson, QC J5B 2E4",
          phone: "450-632-5320",
          hours: "Mer 16H–20H30, Jeu-Dim 11H–21H, Lun-Mar Fermé",
          tagline:
            "Explorez notre menu Delson avec les favoris grecs traditionnels",
        },
      },

      // Home page
      home: {
        heroTitle: "CUISINE GRECQUE AUTHENTIQUE",
        heroDescription:
          "Des plats méditerranéens frais préparés quotidiennement avec des recettes traditionnelles et des ingrédients de qualité. Au service de Montréal et Delson avec l'hospitalité grecque chaleureuse.",
        byow: "Apportez votre vin",
      },

      // Location page
      location: {
        description:
          "Situé au cœur de {{location}}, LA CAVA GRECQUE est un grill méditerranéen grec authentique servant des souvlakis frais, des gyros, de la moussaka, des salades grecques et plus encore dans un cadre chaleureux et traditionnel",
        featuredTitle: "PLATS VEDETTES",
        featuredSubtitle: "Goûtez aux saveurs authentiques de la Grèce",
        aboutTitle: "À PROPOS DE LA CAVA GRECQUE",
        aboutText1:
          "Bienvenue à LA CAVA GRECQUE, où la cuisine grecque traditionnelle rencontre la scène culinaire dynamique de {{location}}. Depuis des années, nous servons des plats méditerranéens authentiques préparés avec les ingrédients les plus frais et des recettes ancestrales transmises de génération en génération.",
        aboutText2:
          "Notre engagement est simple : offrir le vrai goût de la Grèce dans chaque plat. De nos souvlakis parfaitement grillés à notre tzatziki maison, chaque article de notre menu est élaboré avec passion et respect des traditions culinaires grecques.",
        aboutText3:
          "Que vous dîniez sur place, que vous passiez chercher votre commande ou que vous vous fassiez livrer, nous vous promettons une expérience grecque authentique qui transportera vos papilles directement en Méditerranée.",
      },

      // Featured dishes
      featured: {
        souvlaki: {
          name: "SOUVLAKI",
          description:
            "Viande marinée tendre grillée à la perfection, servie avec pita, tzatziki et légumes frais",
        },
        gyros: {
          name: "GYROS",
          description:
            "Viande savoureuse tranchée enveloppée dans un pita chaud avec oignons, tomates et notre sauce tzatziki signature",
        },
        greekSalad: {
          name: "SALADE GRECQUE",
          description:
            "Tomates fraîches, concombres, olives et feta avec huile d'olive grecque authentique et fines herbes",
        },
        moussaka: {
          name: "MOUSSAKA",
          description:
            "Couches d'aubergine, viande assaisonnée et sauce béchamel cuites à la perfection dorée",
        },
      },

      // Menu categories (sections)
      menu: {
        title: "MENU",
        categories: {
          specials: "Spéciaux",
          lunch: "Midi",
          dinner: "Soir",
          grocery: "Épicerie",
          "new-arrivals": "Nouveautés",
        },
        sections: {
          lunch: "MENU MIDI",
          dinner: "MENU PRINCIPAL",
          "new-arrivals": "NOUVEAUTÉS",
          specials: "SPÉCIALES",
          grocery: "ÉPICERIE",
        },
      },

      // Submenu labels
      submenus: {
        appetizers: "Les entrées",
        "main-dishes": "Les plats principaux",
        pitas: "Pitas",
        salads: "Les salades repas",
        pasta: "Les pâtes",
        extras: "Extras",
        beverages: "Breuvages",
        desserts: "Desserts",
        "kids-menu": "Menu pour les enfants (12 ans et moins)",
        "special-cava": "Spécial Cava",
        "red-meat": "Viande Rouge",
        seafood: "Fruits de mer",
        "surf-turf": "Terre et mer",
        "cava-super-bowls": "Super Bols Cava",
        "cava-mini-pitas": "Mini Pitas Cava",
        "cava-new-plates": "Nouveaux Plats Cava",
        "cava-burgers": "Burgers Cava",
        "cava-poutines": "Poutines Cava",
        "for-two": "Pour Deux",
        "chef-special": "Spécial du Chef",
        "lobster-festival": "Festival du Homard",
        "mothers-day-special": "Spécial Fête des Mères",
        "new-pita-specials": "Nouveaux Spéciaux Pitas",
        soups: "Soupes",
        dips: "Trempettes",
        sauces: "Sauces",
        "prepared-meals": "Plats préparés",
      },

      // Order page
      order: {
        title: "COMMANDER EN LIGNE",
        subtitle:
          "Choisissez votre emplacement préféré pour commencer votre commande",
        pickupTitle: "POUR EMPORTER",
        pickupDescription:
          "Commandez à l'avance et récupérez à l'emplacement de votre choix",
        deliveryTitle: "LIVRAISON",
        deliveryDescription:
          "Faites-vous livrer vos plats grecs préférés à votre porte",
        comingSoon: "Commande en ligne bientôt disponible!",
        callToOrder: "En attendant, appelez-nous pour passer votre commande:",
      },

      // 404 page
      notFound: {
        title: "404",
        subtitle: "Page non trouvée",
        description:
          "La page que vous recherchez n'existe pas ou a été déplacée.",
        backHome: "RETOUR À L'ACCUEIL",
      },

      // Footer
      footer: {
        copyright: "© {{year}} LA CAVA GRECQUE. Tous droits réservés.",
      },
    },
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLanguage(),
  fallbackLng: "fr",
  interpolation: {
    escapeValue: false,
  },
});

// Save language preference when changed
i18n.on("languageChanged", (lng) => {
  localStorage.setItem("language", lng);
  // Update HTML lang attribute for accessibility
  document.documentElement.lang = lng;
});

// Set initial HTML lang attribute
document.documentElement.lang = i18n.language;

export default i18n;
